import { css } from '@emotion/css';
import { useState, type FormEvent, type ChangeEvent } from 'react';
import { Check, AlertCircle } from 'lucide-react';
import { useSendContactMessage } from '../../../features/notifications/hooks/notifications-api.hooks';

const form = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  marginBottom: 'var(--spacing-lg)',
});

const formGroup = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const label = css({
  fontWeight: 500,
  fontSize: 'var(--fs-400)',
  color: 'var(--g-800)',
});

const input = css({
  padding: 'var(--spacing-sm)',
  border: '1px solid var(--g-200)',
  borderRadius: '4px',
  fontSize: 'var(--fs-400)',
  width: '100%',
  transition: 'border-color 0.2s',
  backgroundColor: 'var(--primary-color)',
  '&:focus': {
    borderColor: 'var(--pc-400)',
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(var(--pcrgba-400), 0.2)',
  },
});

const textarea = css({
  padding: 'var(--spacing-sm)',
  border: '1px solid var(--g-200)',
  borderRadius: '4px',
  fontSize: 'var(--fs-400)',
  width: '100%',
  transition: 'border-color 0.2s',
  backgroundColor: 'var(--primary-color)',
  resize: 'vertical',
  minHeight: '120px',
  '&:focus': {
    borderColor: 'var(--pc-400)',
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(var(--pcrgba-400), 0.2)',
  },
});

const button = css({
  backgroundColor: 'var(--pc-600)',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: 'var(--spacing-sm) var(--spacing-lg)',
  fontSize: 'var(--fs-400)',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  alignSelf: 'flex-start',
  '&:hover': {
    backgroundColor: 'var(--pc-700)',
  },
  '&:disabled': {
    backgroundColor: 'var(--g-300)',
    cursor: 'not-allowed',
  },
  '@media (max-width: 768px)': {
    width: '100%',
    alignSelf: 'center',
  },
});

const successMessage = css({
  backgroundColor: 'rgba(0, 200, 83, 0.1)',
  color: 'rgb(0, 150, 62)',
  padding: 'var(--spacing-md)',
  borderRadius: '4px',
  marginBottom: 'var(--spacing-md)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
});

const errorMessage = css({
  backgroundColor: 'rgba(220, 53, 69, 0.1)',
  color: '#dc3545',
  padding: 'var(--spacing-md)',
  borderRadius: '4px',
  marginBottom: 'var(--spacing-md)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
});

export interface ContactFormProps {
  defaultName?: string;
  defaultEmail?: string;
  defaultSubject?: string;
  defaultMessage?: string;
  onSuccess?: () => void;
}

export function ContactForm({
  defaultName = '',
  defaultEmail = '',
  defaultSubject = '',
  defaultMessage = '',
  onSuccess,
}: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: defaultName,
    email: defaultEmail,
    subject: defaultSubject,
    message: defaultMessage,
  });

  const sendMessageMutation = useSendContactMessage();

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await sendMessageMutation.mutateAsync(formData);
      
      // Clear form after successful submission
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error submitting contact form:', error);
    }
  };

  return (
    <>
      {sendMessageMutation.isSuccess && (
        <div className={successMessage}>
          <Check size={20} />
          <span>Mensagem enviada com sucesso! Entraremos em contato em breve.</span>
        </div>
      )}

      {sendMessageMutation.isError && (
        <div className={errorMessage}>
          <AlertCircle size={20} />
          <span>
            {(sendMessageMutation.error as any)?.message ||
              'Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente.'}
          </span>
        </div>
      )}

      <form className={form} onSubmit={handleSubmit}>
        <div className={formGroup}>
          <label className={label} htmlFor="name">
            Nome
          </label>
          <input
            className={input}
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Seu nome completo"
            required
          />
        </div>

        <div className={formGroup}>
          <label className={label} htmlFor="email">
            Email
          </label>
          <input
            className={input}
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Seu endereço de email"
            required
          />
        </div>

        <div className={formGroup}>
          <label className={label} htmlFor="subject">
            Assunto
          </label>
          <input
            className={input}
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Assunto da mensagem"
            required
          />
        </div>

        <div className={formGroup}>
          <label className={label} htmlFor="message">
            Mensagem
          </label>
          <textarea
            className={textarea}
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Digite sua mensagem aqui..."
            required
          />
        </div>

        <button className={button} type="submit" disabled={sendMessageMutation.isPending}>
          {sendMessageMutation.isPending ? 'Enviando...' : 'Enviar Mensagem'}
        </button>
      </form>
    </>
  );
}

export default ContactForm;
